import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import type { Paragraph, Root, Text } from 'mdast';
import { $remark } from '@milkdown/kit/utils';
import { SKIP, visit } from 'unist-util-visit';

import { NOTICE_STYLES } from '../nodes/notice-block/notice';
import { VARIABLE_CONTENT_DIRECTIVE_NAME, VARIANT_DIRECTIVE_NAME } from '../nodes/variable-content/constants';

/** Inline text directives handled elsewhere (see embed remark plugin). */
const KNOWN_TEXT_DIRECTIVE_NAMES = new Set(['iframe']);

/** Block leaf directives handled elsewhere (see embed remark plugin). */
const KNOWN_LEAF_DIRECTIVE_NAMES = new Set(['iframe']);

/** Container directives with Milkdown schema handlers (notice + variable content). */
const KNOWN_CONTAINER_DIRECTIVE_NAMES = new Set<string>([...NOTICE_STYLES, VARIABLE_CONTENT_DIRECTIVE_NAME, VARIANT_DIRECTIVE_NAME]);

interface DirectiveMdast {
  type: string;
  name: string;
  attributes?: Record<string, string | null | undefined> | null;
  children?: unknown[];
}

function formatDirectiveAttributes(attributes: DirectiveMdast['attributes']): string {
  if (!attributes) return '';

  const parts = Object.entries(attributes)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => `${key}="${value}"`);

  return parts.length > 0 ? `{${parts.join(' ')}}` : '';
}

function phrasingChildrenToText(children: unknown[] | undefined): string {
  if (!children?.length) return '';

  return children
    .flatMap((child) => {
      if (!child || typeof child !== 'object') return [];
      const node = child as { type?: string; value?: string; children?: unknown[] };

      if (node.type === 'text' && node.value) return [node.value];
      if (node.children) return [phrasingChildrenToText(node.children)];

      return [];
    })
    .join('');
}

function textDirectiveToText(node: DirectiveMdast): Text {
  const inner = phrasingChildrenToText(node.children);
  const bracket = inner ? `[${inner}]` : '';

  return {
    type: 'text',
    value: `:${node.name}${bracket}${formatDirectiveAttributes(node.attributes)}`,
  };
}

function leafDirectiveToParagraph(node: DirectiveMdast) {
  const inner = phrasingChildrenToText(node.children);
  const bracket = inner ? `[${inner}]` : '';

  return {
    type: 'paragraph' as const,
    children: [
      {
        type: 'text' as const,
        value: `::${node.name}${bracket}${formatDirectiveAttributes(node.attributes)}`,
      },
    ],
  };
}

function fenceParagraph(value: string): Paragraph {
  return {
    type: 'paragraph',
    children: [{ type: 'text', value }],
  };
}

/**
 * Unknown `:::name` → open-fence paragraph + body blocks + close-fence paragraph.
 * Preserves inner content; fences become editable text so Milkdown never sees the directive.
 */
function containerDirectiveToLiteralBlocks(node: DirectiveMdast): unknown[] {
  const open = `:::${node.name}${formatDirectiveAttributes(node.attributes)}`;
  const body = Array.isArray(node.children) ? node.children : [];

  return [fenceParagraph(open), ...body, fenceParagraph(':::')];
}

/**
 * `remark-directive` (used for notice callouts) also parses `:word` in prose as a textDirective.
 * Milkdown only registers handlers for known directives; unknown ones crash ParserState.
 * Convert them back to literal text before the schema parser runs.
 */
export function transformUnknownDirectivesInMdast(tree: Root): void {
  visit(tree, 'textDirective', (node, index, parent) => {
    if (index === undefined || !parent) return;

    const directive = node as DirectiveMdast;
    if (KNOWN_TEXT_DIRECTIVE_NAMES.has(directive.name)) return;

    (parent.children as unknown[])[index] = textDirectiveToText(directive);
  });

  visit(tree, 'leafDirective', (node, index, parent) => {
    if (index === undefined || !parent) return;

    const directive = node as DirectiveMdast;
    if (KNOWN_LEAF_DIRECTIVE_NAMES.has(directive.name)) return;
    // Embed-shaped ::loom{src=...} / ::youtube{src=...} — handled by remarkEmbedPlugin.
    if (directive.attributes?.src) return;

    (parent.children as unknown[])[index] = leafDirectiveToParagraph(directive);
  });

  visit(tree, 'containerDirective', (node, index, parent) => {
    if (index === undefined || !parent) return;

    const directive = node as DirectiveMdast;
    if (KNOWN_CONTAINER_DIRECTIVE_NAMES.has(directive.name)) return;

    const replacement = containerDirectiveToLiteralBlocks(directive);
    (parent.children as unknown[]).splice(index, 1, ...replacement);

    // Re-walk from the first inserted node so nested unknown containers in the body are converted too.
    return [SKIP, index];
  });
}

export const remarkUnknownDirectiveFallback = $remark('unknown-directive-fallback', () => {
  return () => (tree: Root) => {
    transformUnknownDirectivesInMdast(tree);
  };
});

export const unknownDirectiveFallbackPlugin: MilkdownPlugin[] = [remarkUnknownDirectiveFallback].flat();
